import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  Heading,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import {
  useDeleteComplianceGroupTransactionStageMutation,
  useGetComplianceGroupTransactionStagesQuery,
  useSaveComplianceGroupTransactionStageByTypeMutation,
} from '@client/graphql/__generated__/document-operations';
import {
  ContractSystemType,
  TransactionStageType,
} from '@client/graphql/__generated__/types';
import { Select } from '@document/common/Select';
import { FC, useMemo, useState } from 'react';
import { CenterSpinner } from '~/common/components/CenterSpinner';
import { useAppContext } from '~/services/main/contexts/AppContext';

import { useDotloopComplianceGroupSections } from './useDotloopComplianceGroupSections';

interface StageOption {
  label: string;
  value: TransactionStageType;
}

const stageTypeLabels: Record<TransactionStageType, string> = {
  [TransactionStageType.LEAD]: 'Lead',
  [TransactionStageType.SIGNED_AGENCY_AGREEMENT]: 'Agency Agreement',
  [TransactionStageType.ACTIVE]: 'Active',
  [TransactionStageType.SUBMITTED_OFFER]: 'Submitted Offer',
  [TransactionStageType.UNDER_CONTRACT]: 'Under Contract',
  [TransactionStageType.CLOSED]: 'Closing',
  [TransactionStageType.TERMINATED]: 'Terminated',
};

const stageOptions: StageOption[] = Object.entries(stageTypeLabels).map(
  ([value, label]) => ({
    value: value as TransactionStageType,
    label,
  })
);

export const OrganizationComplianceGroupStageMapping: FC = () => {
  const { selectedOrganization } = useAppContext();

  const organizationUuid = selectedOrganization?.id ?? '';
  const dotloopProfiles = useMemo(() => {
    const credentials = selectedOrganization?.contractSystemCredentials ?? [];
    const seenProfileIds = new Set<string>();
    const profiles: Array<{ profileId: string; teamName: string }> = [];

    for (const cred of credentials) {
      if (cred.type !== ContractSystemType.DOTLOOP) continue;

      const profileId = cred.dotloopProfileId?.trim();
      if (!profileId) continue;
      if (seenProfileIds.has(profileId)) continue;
      seenProfileIds.add(profileId);

      const teamName = cred.dotloopTeamName?.trim() || 'Dotloop';
      profiles.push({ profileId, teamName });
    }

    return profiles;
  }, [selectedOrganization?.contractSystemCredentials]);

  const {
    dotloopGroupSections,
    loadingDotloopGroups,
    dotloopGroupsLoaded,
    dotloopGroupsError,
    dotloopTeamsWithErrors,
  } = useDotloopComplianceGroupSections(dotloopProfiles);

  const {
    data: mappingsData,
    loading: loadingMappings,
    error: mappingsError,
    refetch: refetchMappings,
  } = useGetComplianceGroupTransactionStagesQuery({
    variables: {
      organizationUuid,
    },
    skip: !organizationUuid,
    fetchPolicy: 'cache-first',
  });

  const [saveMapping, { loading: saving }] =
    useSaveComplianceGroupTransactionStageByTypeMutation();
  const [deleteMapping, { loading: deleting }] =
    useDeleteComplianceGroupTransactionStageMutation();

  const mappingsByComplianceGroupId = useMemo(() => {
    const map = new Map(
      (mappingsData?.getComplianceGroupTransactionStages ?? []).map(
        (mapping) => [mapping.complianceGroupId, mapping] as const
      )
    );

    return map;
  }, [mappingsData]);

  const [selectedStageByGroupId, setSelectedStageByGroupId] = useState<
    Record<string, TransactionStageType | undefined>
  >({});

  if (dotloopProfiles.length === 0) {
    return (
      <Box pt={4}>
        <Alert status="warning">
          <AlertIcon />
          <AlertDescription>
            No Dotloop profile found for this organization.
          </AlertDescription>
        </Alert>
      </Box>
    );
  }

  if (
    loadingMappings ||
    loadingDotloopGroups ||
    (!dotloopGroupsLoaded && dotloopProfiles.length > 0)
  ) {
    return <CenterSpinner />;
  }

  if (dotloopGroupsError) {
    return (
      <Box pt={4}>
        <Alert status="error">
          <AlertIcon />
          <AlertDescription>Failed to load Dotloop workflows.</AlertDescription>
        </Alert>
      </Box>
    );
  }

  if (mappingsError) {
    return (
      <Box pt={4}>
        <Alert status="error">
          <AlertIcon />
          <AlertDescription>Failed to load workflow mappings.</AlertDescription>
        </Alert>
      </Box>
    );
  }

  const totalDotloopGroups = dotloopGroupSections.reduce(
    (acc, next) => acc + next.groups.length,
    0
  );

  if (dotloopGroupsLoaded && totalDotloopGroups === 0) {
    return (
      <Box pt={4}>
        <Alert status="info">
          <AlertIcon />
          <AlertDescription>
            No Dotloop workflows found for this organization.
          </AlertDescription>
        </Alert>
      </Box>
    );
  }

  return (
    <Box pt={4} width="100%">
      <Flex alignItems="baseline" justifyContent="space-between" mb={4}>
        <Heading size="sm">Workflows</Heading>
        <Text color="gray.500" fontSize="sm">
          Workflows are sourced from Dotloop only.
        </Text>
      </Flex>

      {dotloopTeamsWithErrors.length > 0 && (
        <Box mb={4}>
          <Alert status="warning">
            <AlertIcon />
            <AlertDescription>
              Failed to load workflows for: {dotloopTeamsWithErrors.join(', ')}.
            </AlertDescription>
          </Alert>
        </Box>
      )}

      <SimpleGrid columns={1} spacing={6}>
        {dotloopGroupSections.map((section) => (
          <Box key={section.teamName}>
            <Flex alignItems="baseline" justifyContent="space-between" mb={2}>
              <Heading size="xxs">{section.teamName}</Heading>
              <Text color="gray.500" fontSize="sm">
                {section.groups.length} workflow
                {section.groups.length === 1 ? '' : 's'}
              </Text>
            </Flex>

            <SimpleGrid columns={1} spacing={3}>
              {section.groups.map((group) => {
                const existing = mappingsByComplianceGroupId.get(group.id);
                const existingStage =
                  existing?.transactionStageType ?? undefined;
                const selectedStage =
                  selectedStageByGroupId[group.id] ?? existingStage;
                const selectedOption =
                  stageOptions.find((opt) => opt.value === selectedStage) ??
                  null;

                const isBusy = saving || deleting;
                const canSave =
                  Boolean(selectedStage) && selectedStage !== existingStage;

                return (
                  <Flex
                    key={group.id}
                    alignItems="center"
                    borderColor="gray.200"
                    borderRadius="md"
                    borderWidth="1px"
                    gap={3}
                    p={3}
                  >
                    <Box flex={1} minWidth={0}>
                      <Text fontWeight="semibold" noOfLines={1}>
                        {group.name}
                      </Text>
                      <Text color="gray.500" fontSize="sm" noOfLines={1}>
                        {group.id}
                      </Text>
                    </Box>

                    <Box minWidth="260px">
                      <Select<StageOption>
                        getOptionLabel={(opt) => opt.label}
                        getOptionValue={(opt) => opt.value}
                        isSearchable={false}
                        options={stageOptions}
                        placeholder="Select stage…"
                        size="sm"
                        value={selectedOption}
                        onChange={(value) => {
                          setSelectedStageByGroupId((prev) => ({
                            ...prev,
                            [group.id]: value?.value,
                          }));
                        }}
                      />
                    </Box>

                    <Button
                      isDisabled={!canSave || isBusy}
                      isLoading={saving}
                      size="sm"
                      onClick={() => {
                        if (!selectedStage) return;
                        void saveMapping({
                          variables: {
                            input: {
                              organizationUuid,
                              complianceGroupId: group.id,
                              complianceGroupName: group.name,
                              transactionStageType: selectedStage,
                            },
                          },
                        }).then(() => refetchMappings());
                      }}
                    >
                      Save
                    </Button>

                    <Button
                      isDisabled={!existing || isBusy}
                      isLoading={deleting}
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        if (!existing?.id) return;
                        void deleteMapping({
                          variables: {
                            id: existing.id,
                          },
                        }).then(() => {
                          setSelectedStageByGroupId((prev) => {
                            const next = { ...prev };
                            delete next[group.id];

                            return next;
                          });

                          return refetchMappings();
                        });
                      }}
                    >
                      Clear
                    </Button>
                  </Flex>
                );
              })}
            </SimpleGrid>
          </Box>
        ))}
      </SimpleGrid>
    </Box>
  );
};
